import cx from "classnames";
import { ReactNode, useEffect, useRef } from "react";

import { useTranslation } from "@/core/i18n";
import { useImagePreload, UseImagePreloadOptions } from "hooks/useImagePreload";

interface ImagePreloaderProps {
  /** Additional classes */
  "className"?: string;
  /** The URL of the image to preload */
  "url": string;
  /** Content to show when image is loaded */
  "children": ReactNode;
  /** Content to show while image is loading */
  "placeholder"?: ReactNode;
  /** Content to show when image fails to load */
  "errorPlaceholder"?: ReactNode;
  /** Fade transition duration in milliseconds (0 = no fade) */
  "fade"?: number;
  /** Enable lazy loading (intersection observer) */
  "lazy"?: boolean;
  /** Distance from viewport edge when lazy loading should start */
  "preloadDistance"?: string;
  /** Minimum load time in ms before fade is applied (default: 100ms) */
  "fadeThreshold"?: number;
  /** Specify the testid for current component */
  "data-testid"?: string;
}

export const ImagePreloader = ({
  url,
  children,
  className,
  placeholder,
  errorPlaceholder,
  fade = 50,
  lazy = false,
  preloadDistance = "50px",
  fadeThreshold = 50,
  "data-testid": dataTestId,
}: ImagePreloaderProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const options: UseImagePreloadOptions = {
    lazy,
    preloadDistance,
    fadeThreshold,
  };

  const { status, shouldFade, setRef } = useImagePreload(url, options);

  // Set up intersection observer ref
  useEffect(() => {
    if (containerRef.current) {
      setRef(containerRef.current);
    }
  }, [setRef]);

  // Track if this URL has completed loading at least once to prevent repeat fades
  const hasLoadedOnceRef = useRef(false);
  const prevUrlRef = useRef<string | null>(null);

  // Reset on URL change
  useEffect(() => {
    if (prevUrlRef.current !== url) {
      prevUrlRef.current = url;
      hasLoadedOnceRef.current = false;
    }
  }, [url]);

  // Mark as loaded once when status transitions to loaded
  useEffect(() => {
    if (status === "loaded") {
      hasLoadedOnceRef.current = true;
    }
  }, [status]);

  // Determine what to show
  const getContent = () => {
    if (status === "error") {
      return (
        errorPlaceholder ??
        placeholder ?? (
          <div className="flex h-full w-full items-center justify-center bg-gray-200 text-sm text-gray-500">
            {t("imagePreloader.failedToLoad")}
          </div>
        )
      );
    }

    if (status === "loaded") {
      return children;
    }

    if (status === "loading" || lazy) {
      return placeholder ?? <div className="h-full w-full bg-transparent" />;
    }

    return placeholder ?? <div className="h-full w-full bg-transparent" />;
  };

  const content = getContent();

  // CSS transition styles - only use fade if shouldFade is true
  // For cached images or after first load, skip fade logic completely
  const hasFade = fade > 0;
  const hasFadeThisRender = hasFade && !hasLoadedOnceRef.current;
  const shouldUseFade = hasFadeThisRender && shouldFade;

  // Content fade-in style for fade-on-top approach
  // Content fades in on top of placeholder when loaded
  const contentFadeStyle = shouldUseFade
    ? {
        transition: `opacity ${fade}ms ease-in-out`,
        opacity: status === "loaded" ? 1 : 0,
      }
    : {
        // No transition after first load, keep opacity at 1 once loaded
        opacity: status === "loaded" || hasLoadedOnceRef.current ? 1 : 0,
      };

  // Handle error state immediately - no transition needed
  if (status === "error") {
    const errorContent = errorPlaceholder ?? placeholder ?? (
      <div className="flex h-full w-full items-center justify-center bg-gray-200 text-sm text-gray-500">
        {t("imagePreloader.failedToLoad")}
      </div>
    );

    return (
      <div ref={containerRef} className={cx("relative", className)} data-testid={dataTestId}>
        {errorContent}
      </div>
    );
  }

  // Main rendering logic - fade vs no-fade
  return (
    <div
      ref={containerRef}
      className={cx("relative", className)}
      data-testid={dataTestId}
      style={{
        // Ensure smooth transitions don't cause layout shift
        minHeight: hasFadeThisRender ? "1px" : undefined,
      }}
    >
      {hasFadeThisRender ? (
        <>
          {/* Placeholder layer - static layout foundation, always visible */}
          <div className="h-full w-full">
            {placeholder ?? <div className="h-full w-full bg-transparent" />}
          </div>

          {/* Content layer - fades in on top when loaded */}
          <div className="absolute inset-0" style={contentFadeStyle}>
            {children}
          </div>
        </>
      ) : (
        content
      )}
    </div>
  );
};
